import React, { useState, useEffect } from 'react';
import { 
  getExperience, 
  addExperience, 
  updateExperience, 
  deleteExperience,
  addExperienceResponsibility,
  updateResponsibility,
  deleteResponsibility
} from '../../services/profile';
import { FaEdit, FaTrashAlt, FaPlus, FaTimes, FaSave, FaBriefcase } from 'react-icons/fa';
import './Experience.css';

const Experience = ({profileSection, profileData, setCompletion }) => {

  console.log(profileData)

  const [experienceList, setExperienceList] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(null);
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // State for new/editing experience
  const [experienceForm, setExperienceForm] = useState({
    company_name: '',
    job_title: '',
    start_date: '',
    end_date: '',
    is_current: false,
    location: '',
    responsibilities: []
  });

  // Fetch experiences on component mount
  useEffect(() => {
    const fetchExperiences = async () => {
      try {
        setIsLoading(true);
        const response = await getExperience(profileData?.profile_id);
        setExperienceList(response);
      } catch (error) {
        console.error('Error fetching experiences:', error);
        setFormError('Failed to load experiences. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchExperiences();
  }, []);

  
  useEffect(() => {
    setCompletion("experience", experienceList);
  }, [experienceList])

  // Clear form data
  const clearForm = () => {
    setExperienceForm({
      company_name: '',
      job_title: '',
      start_date: '',
      end_date: '',
      is_current: false,
      location: '',
      responsibilities: []
    });
    setFormError('');
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExperienceForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // If setting current job to true, clear end date
    if (name === 'is_current' && checked) {
      setExperienceForm(prev => ({
        ...prev,
        end_date: ''
      }));
    }
  };

  // Handle responsibilities input
  const handleResponsibilitiesChange = (e) => {
    const responsibilitiesText = e.target.value;
    // Split by new line and filter empty strings
    const responsibilitiesArray = responsibilitiesText
      .split('\n')
      .filter(item => item.trim())
      .map(item => ({ responsibility: item.trim() }));
      
    setExperienceForm(prev => ({
      ...prev,
      responsibilities: responsibilitiesArray
    }));
  };

  // Get responsibilities as text for the form
  const getResponsibilitiesText = () => {
    if (!experienceForm.responsibilities || experienceForm.responsibilities.length === 0) {
      return '';
    }
    
    return experienceForm.responsibilities
      .map(r => typeof r === 'string' ? r : r.responsibility)
      .join('\n');
  };

  // Add new experience
  const handleAddExperience = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!experienceForm.company_name || !experienceForm.job_title || !experienceForm.start_date || 
        (!experienceForm.end_date && !experienceForm.is_current)) {
      setFormError('Please fill out all required fields');
      return;
    }
    
    try {
      // Extract responsibilities from form
      const responsibilities = experienceForm.responsibilities.map(r => 
        typeof r === 'string' ? r : r.responsibility
      );
      
      // Format data for API
      const experienceData = {
        company_name: experienceForm.company_name,
        job_title: experienceForm.job_title,
        start_date: experienceForm.start_date,
        end_date: experienceForm.is_current ? null : experienceForm.end_date,
        is_current: experienceForm.is_current,
        location: experienceForm.location,
        responsibilities: responsibilities
      };
      
      // Send to API
      const response = await addExperience(experienceData, profileData?.profile_id);
      
      // Update local state with response
      setExperienceList(prev => [...prev, response]);
      
      // Reset form and state
      clearForm();
      setIsAdding(false);
    } catch (error) {
      console.error('Error adding experience:', error);
      setFormError('Failed to add experience. Please try again.');
    }
  };

  // Start editing an experience
  const handleEditStart = (experience) => {
    setIsEditing(experience.id);
    
    // Format responsibilities for the form
    const responsibilitiesForForm = experience.responsibilities || [];
    
    setExperienceForm({
      company_name: experience.company_name,
      job_title: experience.job_title,
      start_date: experience.start_date,
      end_date: experience.end_date || '',
      is_current: experience.is_current,
      location: experience.location || '',
      responsibilities: responsibilitiesForForm
    });
  };

  // Save edited experience
  const handleEditSave = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!experienceForm.company_name || !experienceForm.job_title || !experienceForm.start_date || 
        (!experienceForm.end_date && !experienceForm.is_current)) {
      setFormError('Please fill out all required fields');
      return;
    }
    
    try {
      // Format data for API
      const experienceData = {
        company_name: experienceForm.company_name,
        job_title: experienceForm.job_title,
        start_date: experienceForm.start_date,
        end_date: experienceForm.is_current ? null : experienceForm.end_date,
        is_current: experienceForm.is_current,
        location: experienceForm.location
      };
      
      // Update the basic experience information
      const response = await updateExperience(isEditing, experienceData, profileData?.profile_id);
      
      // Get the existing experience to compare responsibilities
      const existingExperience = experienceList.find(exp => exp.id === isEditing);
      const existingResponsibilities = existingExperience?.responsibilities || [];
      
      // Get the current responsibilities from the form
      const currentResponsibilitiesText = getResponsibilitiesText().split('\n');
      
      // Process existing responsibilities
      for (const existingResp of existingResponsibilities) {
        // If responsibility exists in the form, update it if needed
        const matchingTextIndex = currentResponsibilitiesText.findIndex(
          text => text === existingResp.responsibility
        );
        
        if (matchingTextIndex >= 0) {
          // Responsibility still exists, we don't need to update it
          // Remove it from the text array to mark it as processed
          currentResponsibilitiesText.splice(matchingTextIndex, 1);
        } else {
          // Responsibility was deleted, remove it from the API
          await deleteResponsibility(existingResp.id, profileData?.profile_id);
        }
      }
      
      // Add new responsibilities that weren't in the original list
      for (const newRespText of currentResponsibilitiesText) {
        if (newRespText.trim()) {
          await addExperienceResponsibility(isEditing, { 
            responsibility: newRespText 
          }, profileData?.profile_id);
        }
      }
      
      // Fetch the updated experience to get all responsibilities with their IDs
      const updatedExperience = await getExperience(profileData?.profile_id);
      const updatedExperienceItem = updatedExperience.find(exp => exp.id === isEditing);
      
      // Update local state
      setExperienceList(prev => 
        prev.map(exp => exp.id === isEditing ? updatedExperienceItem : exp)
      );
      
      // Reset form and state
      clearForm();
      setIsEditing(null);
    } catch (error) {
      console.error('Error updating experience:', error);
      setFormError('Failed to update experience. Please try again.');
    }
  };

  // Cancel adding/editing
  const handleCancel = () => {
    clearForm();
    setIsAdding(false);
    setIsEditing(null);
  };

  // Delete experience
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this experience?')) {
      try {
        await deleteExperience(id, profileData?.profile_id);
        setExperienceList(prev => prev.filter(exp => exp.id !== id));
      } catch (error) {
        console.error('Error deleting experience:', error);
        setFormError('Failed to delete experience. Please try again.');
      }
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="experience-loading">
        <div className="loading-spinner"></div>
        <p>Loading work experiences...</p>
      </div>
    );
  }

  return (
    <div className="experience-section">
      <div className="section-header">
        <h2><div className="section-icon">
          {profileSection.icon}
        </div> Work Experience</h2>
        {!isAdding && !isEditing && (
          <button 
            onClick={() => setIsAdding(true)}
            className="btn-add"
          >
            <FaPlus /> Add Experience
          </button>
        )}
      </div>
      
      {/* Experience List */}
      {experienceList?.length > 0 ? (
        <div className="experience-list">
          {experienceList?.map((experience) => (
            <div key={experience?.id} className="experience-item">
              {isEditing === experience?.id ? (
                <form onSubmit={handleEditSave} className="experience-form">
                  <h3>Edit Experience</h3>
                  {formError && <div className="form-error">{formError}</div>}
                  
                  <div className="form-group">
                    <label htmlFor="company_name">Company *</label>
                    <input
                      type="text"
                      id="company_name"
                      name="company_name"
                      value={experienceForm.company_name}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="job_title">Job Title *</label>
                    <input
                      type="text"
                      id="job_title"
                      name="job_title"
                      value={experienceForm.job_title}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="start_date">Start Date *</label>
                      <input
                        type="date"
                        id="start_date"
                        name="start_date"
                        value={experienceForm.start_date}
                        onChange={handleInputChange}
                        required
                        className="form-control"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="end_date">End Date {!experienceForm.is_current && '*'}</label>
                      <input
                        type="date"
                        id="end_date"
                        name="end_date"
                        value={experienceForm.end_date}
                        onChange={handleInputChange}
                        disabled={experienceForm.is_current}
                        required={!experienceForm.is_current}
                        className="form-control"
                      />
                    </div>
                    <div className="form-group checkbox">
                    <div className="current-checkbox">
                        <input
                      type="checkbox"
                      id="is_current"
                      name="is_current"
                      checked={experienceForm.is_current}
                      onChange={handleInputChange}
                      className="checkbox-input"
                    />
                    <label htmlFor="is_current" className="checkbox-label">I currently work here</label>
                  </div>
                  </div>
                  </div>
                  
                  
                  
                  <div className="form-group">
                    <label htmlFor="location">Location</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={experienceForm.location}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="responsibilities">Responsibilities *</label>
                    <textarea
                      id="responsibilities"
                      name="responsibilities"
                      value={getResponsibilitiesText()}
                      onChange={handleResponsibilitiesChange}
                      rows="4"
                      required
                      className="form-control"
                    />
                    <small className="form-text">Separate each responsibility with a new line</small>
                  </div>
                  
                  <div className="form-actions">
                    <button type="button" onClick={handleCancel} className="btn-secondary">
                      <FaTimes /> Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      <FaSave /> Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                <div className="experience-display">
                  <div className="experience-header">
                    <div className="experience-title">
                      <h3>{experience.job_title}</h3>
                      <h4>{experience.company_name}</h4>
                      <p className="experience-dates">
                        {formatDate(experience.start_date)} - {experience.is_current ? 'Present' : formatDate(experience.end_date)}
                      </p>
                      {experience.location && <p className="experience-location">{experience.location}</p>}
                    </div>
                    <div className="experience-actions">
                      <button 
                        onClick={() => handleEditStart(experience)}
                        className="btn-icon edit"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        onClick={() => handleDelete(experience.id)}
                        className="btn-icon delete"
                        title="Delete"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </div>
                  
                  <div className="experience-details">
                    <div className="experience-responsibilities">
                      <h5>Responsibilities:</h5>
                      <ul className="responsibility-list">
                        {experience.responsibilities?.map((resp, index) => (
                          <li key={index}>{typeof resp === 'string' ? resp : resp.responsibility}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-list">
          <p>No work experiences added yet.</p>
          <p>Click "Add Experience" to get started.</p>
        </div>
      )}
      
      {/* Add Experience Form */}
      {isAdding && (
        <div className="add-experience">
          <form onSubmit={handleAddExperience} className="experience-form">
            <h3>Add New Experience</h3>
            {formError && <div className="form-error">{formError}</div>}
            
            <div className="form-group">
              <label htmlFor="company_name">Company *</label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={experienceForm.company_name}
                onChange={handleInputChange}
                required
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="job_title">Job Title *</label>
              <input
                type="text"
                id="job_title"
                name="job_title"
                value={experienceForm.job_title}
                onChange={handleInputChange}
                required
                className="form-control"
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="start_date">Start Date *</label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={experienceForm.start_date}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="end_date">End Date {!experienceForm.is_current && '*'}</label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={experienceForm.end_date}
                  onChange={handleInputChange}
                  disabled={experienceForm.is_current}
                  required={!experienceForm.is_current}
                  className="form-control"
                />
              </div>
              <div className="form-group checkbox">
              <div className="current-checkbox">
                        <input
                type="checkbox"
                id="is_current"
                name="is_current"
                checked={experienceForm.is_current}
                onChange={handleInputChange}
                className="checkbox-input"
              />
              <label htmlFor="is_current" className="checkbox-label">I currently work here</label>
            </div>
            </div>
            </div>
            
            
            
            <div className="form-group">
              <label htmlFor="location">Location</label>
              <input
                type="text"
                id="location"
                name="location"
                value={experienceForm.location}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="responsibilities">Responsibilities *</label>
              <textarea
                id="responsibilities"
                name="responsibilities"
                value={getResponsibilitiesText()}
                onChange={handleResponsibilitiesChange}
                rows="4"
                required
                className="form-control"
              />
              <small className="form-text">Separate each responsibility with a new line</small>
            </div>
            
            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="btn-secondary">
                <FaTimes /> Cancel
              </button>
              <button type="submit" className="btn-primary">
                <FaPlus /> Add Experience
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Experience;